/**
 * Offer (discount) time-window arithmetic — pure, no React.
 *
 * Extracted from `useActiveDiscount` so non-browser callers can share the exact
 * same definition of "this offer is live right now": the Bun server's public
 * agent API (`server/publicApi.ts`) answers `live_now` for every offer it
 * publishes, and two implementations would eventually disagree — which, on a
 * surface agents quote, is a truthfulness bug, not a cosmetic one.
 *
 * Everything here reads WALL-CLOCK local time (`getHours`, `getDay`) because
 * `starts_at` / `ends_at` are restaurant-local "HH:MM" strings. Callers must
 * therefore run in Romanian time: browsers are the diner's device (by design),
 * and the server pins `TZ=Europe/Bucharest` (Dockerfile + fly.toml).
 *
 * The hook still owns the ticking/countdown; this module owns the maths.
 */

import type { Discount } from "@/types/restaurant";

/**
 * The fields the window maths actually reads. Typed as a subset so a caller
 * holding a narrower row — the public API selects only the columns it may
 * publish — can use these without casting a partial row to a full `Discount`.
 */
export type DiscountWindow = Pick<
  Discount,
  "active" | "repeating" | "days_of_week" | "starts_at" | "ends_at" | "created_at"
>;

const DAY_MS = 24 * 60 * 60 * 1000;

function parseHHMM(s: string): { h: number; m: number } | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (Number.isNaN(h) || Number.isNaN(min)) return null;
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return { h, m: min };
}

function atTime(ref: Date, hhmm: string): Date | null {
  const parsed = parseHHMM(hhmm);
  if (!parsed) return null;
  const out = new Date(ref);
  out.setHours(parsed.h, parsed.m, 0, 0);
  return out;
}

/**
 * The single [start, end] occurrence of a one-time offer, anchored on its
 * `created_at`: the first date on/after creation whose [starts_at, ends_at]
 * window (on an allowed weekday) ends at or after creation. Deterministic, so
 * the consumer can decide "already happened?" without any server write.
 */
function oneTimeWindow(d: DiscountWindow): { start: Date; end: Date } | null {
  const sp = parseHHMM(d.starts_at);
  const ep = parseHHMM(d.ends_at);
  if (!sp || !ep) return null;
  const created = new Date(d.created_at);
  if (Number.isNaN(created.getTime())) return null;
  const days = d.days_of_week && d.days_of_week.length > 0 ? d.days_of_week : null;

  for (let i = 0; i < 8; i++) {
    const day = new Date(created);
    day.setDate(created.getDate() + i);
    day.setHours(0, 0, 0, 0);
    if (days && !days.includes(day.getDay())) continue;
    const start = new Date(day);
    start.setHours(sp.h, sp.m, 0, 0);
    let end = new Date(day);
    end.setHours(ep.h, ep.m, 0, 0);
    if (end.getTime() <= start.getTime()) end = new Date(end.getTime() + DAY_MS); // midnight wrap
    if (end.getTime() >= created.getTime()) return { start, end };
  }
  return null;
}

/** True once a one-time offer's single occurrence has fully passed. */
export function isOneTimeEnded(d: DiscountWindow, now: Date): boolean {
  if (d.repeating !== false) return false;
  const w = oneTimeWindow(d);
  return !!w && now.getTime() >= w.end.getTime();
}

/** ms remaining + expiry if `d` is active and `now` is inside its window, else null. */
export function evaluateDiscount(
  d: DiscountWindow,
  now: Date,
): { msRemaining: number; expiresAt: Date } | null {
  if (!d.active) return null;

  // One-time offers ignore weekly recurrence: they live only inside their single
  // computed occurrence and never again.
  if (d.repeating === false) {
    const w = oneTimeWindow(d);
    if (!w) return null;
    if (now.getTime() >= w.start.getTime() && now.getTime() < w.end.getTime()) {
      return { msRemaining: w.end.getTime() - now.getTime(), expiresAt: w.end };
    }
    return null;
  }

  // days_of_week null/empty = every day.
  if (d.days_of_week && d.days_of_week.length > 0 && !d.days_of_week.includes(now.getDay())) {
    return null;
  }

  const startToday = atTime(now, d.starts_at);
  const endToday = atTime(now, d.ends_at);
  if (!startToday || !endToday) return null;

  let start = startToday;
  let end = endToday;

  // Midnight-wrap support: window like 23:00–02:00.
  if (end.getTime() <= start.getTime()) {
    if (now.getTime() >= start.getTime()) {
      end = new Date(end.getTime() + DAY_MS);
    } else {
      start = new Date(start.getTime() - DAY_MS);
    }
  }

  if (now.getTime() >= start.getTime() && now.getTime() < end.getTime()) {
    return { msRemaining: end.getTime() - now.getTime(), expiresAt: end };
  }
  return null;
}

/**
 * Pure predicate: is `d` active *and* currently inside its time-window for
 * `now`? Shared by the Offers tab (live-first sort + "Live now" pill), the
 * detail modal, the card badge, and the public agent API — so they all agree
 * on what "live" means.
 */
export function isDiscountLive(d: DiscountWindow, now: Date): boolean {
  return evaluateDiscount(d, now) !== null;
}
